import { useEffect, useState } from "react";

export const CONSENT_COOKIE_NAME = "cookie_consent";
export const CONSENT_VERSION = "1.0.0";
export const CONSENT_EVENT_NAME = "upadlosci:cookie-consent";
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const CONSENT_REGISTER_KEY = "upadlosci-cookie-consent-register";

export type CookieCategoryId =
  | "essential"
  | "functional"
  | "analytics"
  | "marketing";
export type CookiePreferences = Record<CookieCategoryId, boolean>;

export type ConsentRecord = CookiePreferences & {
  version: string;
  timestamp: string;
  eventId: string;
};

export type CookieDeclaration = {
  name: string;
  provider: string;
  purpose: string;
  duration: string;
};

export type CookieCategory = {
  id: CookieCategoryId;
  label: string;
  description: string;
  locked?: boolean;
  declarations: CookieDeclaration[];
  noCookiesMessage?: string;
};

export const defaultCookiePreferences: CookiePreferences = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
};

export const cookieCategories: CookieCategory[] = [
  {
    id: "essential",
    label: "Niezbędne",
    description:
      "Zapamiętują Twój wybór dotyczący cookies i umożliwiają podstawowe działanie serwisu.",
    locked: true,
    declarations: [
      {
        name: "cookie_consent",
        provider: "Upadłości Ekspert",
        purpose: "Zapis wyboru kategorii cookies.",
        duration: "12 miesięcy",
      },
    ],
  },
  {
    id: "functional",
    label: "Funkcjonalne",
    description:
      "Pozwalają na świadome załadowanie treści zewnętrznych, takich jak mapa oraz wywiad.",
    declarations: [],
    noCookiesMessage:
      "Mapa jest ładowana w trybie bez danych uwierzytelniających, dlatego nie zapisuje plików cookies dostawcy w przeglądarce.",
  },
  {
    id: "analytics",
    label: "Analityczne",
    description:
      "Pomagają oceniać korzystanie z serwisu i poprawiać jego działanie.",
    declarations: [],
    noCookiesMessage:
      "Skrypty analityczne mogą zostać uruchomione wyłącznie po Twojej zgodzie.",
  },
  {
    id: "marketing",
    label: "Marketingowe",
    description:
      "Umożliwiają uruchomienie tagów marketingowych i pomiaru skuteczności kampanii.",
    declarations: [
      {
        name: "_ga",
        provider: "Google Analytics 4",
        purpose: "Rozróżnianie użytkowników i pomiar korzystania z serwisu.",
        duration: "2 lata",
      },
      {
        name: "_ga_<identyfikator>",
        provider: "Google Analytics 4",
        purpose:
          "Utrzymanie stanu sesji i pomiar zdarzeń w konfiguracji Google.",
        duration: "2 lata",
      },
      {
        name: "_gcl_au",
        provider: "Google Ads",
        purpose: "Pomiar skuteczności reklam i atrybucja konwersji.",
        duration: "90 dni",
      },
      {
        name: "IDE",
        provider: "Google / DoubleClick",
        purpose:
          "Pomiar i personalizacja reklam, gdy odpowiednie tagi są aktywne w kontenerze GTM.",
        duration: "13 miesięcy",
      },
      {
        name: "test_cookie",
        provider: "Google / DoubleClick",
        purpose: "Sprawdzenie, czy przeglądarka obsługuje pliki cookies.",
        duration: "15 minut",
      },
    ],
  },
];

function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function parseRecord(value: string | undefined): ConsentRecord | undefined {
  if (!value) return undefined;
  try {
    const candidate = JSON.parse(
      decodeURIComponent(value)
    ) as Partial<ConsentRecord>;
    if (
      candidate.version !== CONSENT_VERSION ||
      typeof candidate.timestamp !== "string" ||
      typeof candidate.eventId !== "string" ||
      candidate.essential !== true ||
      typeof candidate.functional !== "boolean" ||
      typeof candidate.analytics !== "boolean" ||
      typeof candidate.marketing !== "boolean"
    )
      return undefined;
    return candidate as ConsentRecord;
  } catch {
    return undefined;
  }
}

function cookieValue(name: string) {
  if (!isBrowser()) return undefined;
  return document.cookie
    .split(";")
    .map(item => item.trim())
    .find(item => item.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

export function getConsentRecord() {
  return parseRecord(cookieValue(CONSENT_COOKIE_NAME));
}

export function hasCookieCategory(category: CookieCategoryId) {
  return getConsentRecord()?.[category] === true;
}

function storedPreferences() {
  if (!isBrowser()) return defaultCookiePreferences;
  return getConsentRecord() ?? defaultCookiePreferences;
}

function deleteOptionalCookies() {
  if (!isBrowser()) return;
  const names = document.cookie
    .split(";")
    .map(item => item.trim().split("=")[0])
    .filter(name =>
      /^(?:_ga(?:_|$)|_gid$|_gat(?:_|$)|_gcl(?:_|$)|_fbp$|_hj|_pin|IDE$|test_cookie$)/i.test(
        name
      )
    );
  const hostParts = window.location.hostname.split(".");
  const rootDomain =
    hostParts.length > 1 ? `.${hostParts.slice(-2).join(".")}` : "";
  for (const name of names) {
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
    if (rootDomain)
      document.cookie = `${name}=; Max-Age=0; Path=/; Domain=${rootDomain}; SameSite=Lax`;
  }

  [
    "upadlosci-cookie-consent",
    "umami.disabled",
    "umami.enabled",
    "_ga",
    "_gid",
    "_gcl_au",
    "_fbp",
  ].forEach(key => {
    try {
      window.localStorage.removeItem(key);
      window.sessionStorage.removeItem(key);
    } catch {
      // Browser storage may be unavailable.
    }
  });

  try {
    for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
      const key = window.localStorage.key(index);
      if (key && /^(?:_ga(?:_|$)|_gcl(?:_|$)|_fbp$|_hj|_pin)/i.test(key))
        window.localStorage.removeItem(key);
    }
    for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = window.sessionStorage.key(index);
      if (key && /^(?:_ga(?:_|$)|_gcl(?:_|$)|_fbp$|_hj|_pin)/i.test(key))
        window.sessionStorage.removeItem(key);
    }
  } catch {
    // Browser storage may be unavailable.
  }
}

function getConsentRegister() {
  if (!isBrowser()) return [] as ConsentRecord[];
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(CONSENT_REGISTER_KEY) ?? "[]"
    ) as unknown;
    if (!Array.isArray(parsed)) return [] as ConsentRecord[];
    return parsed.filter(
      (entry): entry is ConsentRecord =>
        typeof entry === "object" && entry !== null && "eventId" in entry
    );
  } catch {
    return [] as ConsentRecord[];
  }
}

function saveConsentRegister(record: ConsentRecord) {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(
      CONSENT_REGISTER_KEY,
      JSON.stringify([...getConsentRegister(), record].slice(-100))
    );
  } catch {
    // A full or unavailable local storage must not prevent saving the required cookie.
  }
}

export function saveCookieConsent(nextPreferences: CookiePreferences) {
  if (!isBrowser()) return undefined;
  const record: ConsentRecord = {
    ...nextPreferences,
    essential: true,
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    eventId:
      window.crypto?.randomUUID?.() ??
      `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  };
  document.cookie = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(record))}; Max-Age=${CONSENT_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${window.location.protocol === "https:" ? "; Secure" : ""}`;
  if (!record.analytics || !record.marketing) deleteOptionalCookies();
  saveConsentRegister(record);
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT_NAME, { detail: record }));
  return record;
}

export function grantFunctionalConsent() {
  const current = getConsentRecord();
  return saveCookieConsent({
    ...defaultCookiePreferences,
    ...(current ?? {}),
    essential: true,
    functional: true,
  });
}

export function downloadConsentRegister() {
  if (!isBrowser()) return;
  const header =
    "timestamp,version,event_id,essential,functional,analytics,marketing";
  const records = getConsentRegister().map(record =>
    [
      record.timestamp,
      record.version,
      record.eventId,
      record.essential,
      record.functional,
      record.analytics,
      record.marketing,
    ].join(",")
  );
  const file = new Blob([[header, ...records].join("\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.download = "rejestr-zgod-cookies.csv";
  link.click();
  URL.revokeObjectURL(url);
}

export function useCookiePreferences() {
  const [preferences, setPreferences] =
    useState<CookiePreferences>(storedPreferences);

  useEffect(() => {
    const update = (record?: ConsentRecord) =>
      setPreferences(record ?? getConsentRecord() ?? defaultCookiePreferences);
    update();
    const handleChange = (event: Event) =>
      update((event as CustomEvent<ConsentRecord>).detail);
    window.addEventListener(CONSENT_EVENT_NAME, handleChange);
    return () => window.removeEventListener(CONSENT_EVENT_NAME, handleChange);
  }, []);

  return preferences;
}
